import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

export const AsyncProductSelect = ({
  onChange,
  value,
  required,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [products, setProducts] = useState<string[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>(value || '');
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/product`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch products: ${response.statusText}${errorText && errorText.trim() ? ` (${errorText.trim().slice(0, 200)})` : ''}`
        );
      }

      const productList = await response.json();
      setProducts(Array.isArray(productList) ? productList : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch products.');
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newProduct = event.target.value as string;
    setSelectedProduct(newProduct);
    onChange?.(newProduct);
  };

  return (
    <FormControl margin="normal" required={required} error={!!error} fullWidth>
      <InputLabel id="product-select-label">Product</InputLabel>
      <Select labelId="product-select-label" value={selectedProduct} onChange={handleChange}>
        {products.map((product) => (
          <MenuItem key={product} value={product}>
            {product}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!error && <FormHelperText>Select a product from the list.</FormHelperText>}
    </FormControl>
  );
};